// SINGLE SOURCE OF TRUTH for Form 2290 tax periods and filing deadlines.
//
// WHY THIS FILE EXISTS
// Before it, TY2026 due dates lived in two places (a `DUE_DATES` array in
// HowItWorks.jsx and a copy inside blogPosts.js), and HowItWorks paired them
// with `MONTHS` from rates.js *by array index* - so any edit to either list
// silently shifted every row to the wrong deadline. Publishing a wrong filing
// deadline on a tax site is the worst class of error this project can make: the
// reader misses a federal deadline and pays a penalty.
//
// ─────────────────────────────────────────────────────────────────────────────
// THIS DATA IS TRANSCRIBED FROM THE IRS. IT IS NOT COMPUTED.
//
// Per form2290-api/CLAUDE.md: authoritative sources only, no guessing. The
// deadline rule is "the last day of the month following the month of first use",
// rolled forward off weekends AND federal holidays - and that roll genuinely
// bites (e.g. May 31 2027 is Memorial Day, so April 2027 first-use is due
// June 1 2027, not May 31). We deliberately do NOT derive these dates from our
// own holiday algorithm: if our algorithm and the IRS ever disagree, we would
// publish a wrong date with total confidence.
//
// `scripts/check-tax-period.mjs` independently RE-COMPUTES each date and warns
// on any mismatch. That check exists to catch transcription typos - it never
// overrides the transcribed value.
// ─────────────────────────────────────────────────────────────────────────────
//
// ANNUAL MAINTENANCE - required every year, around May/June:
//   1. Read the new "When To File" table from the current IRS Instructions for
//      Form 2290 (i2290).
//   2. Add a new TAX_PERIODS entry. Do not edit an old one - prior-year returns
//      are still filed, and /blog articles cite historical periods.
//   3. Update CURRENT_TAX_YEAR.
//   4. Set `verifiedOn` and `source` to what you actually checked.
//   5. Run `npm run check:tax-period` - it fails the build if the current period
//      has expired, so a forgotten update cannot ship silently.

export const TAX_PERIODS = {
  TY2026: {
    taxYear: "TY2026",
    label: "2026–2027",
    periodStart: "2026-07-01",
    periodEnd: "2027-06-30",
    source: "IRS Instructions for Form 2290 (Rev. July 2026) — When To File",
    verifiedOn: "2026-08-07",
    // monthsRemaining drives partial-period tax: a vehicle first used after July
    // is taxed only for the months left in the period.
    dueDates: [
      { month: "2026-07", monthLabel: "July 2026",      monthsRemaining: 12, dueDate: "2026-08-31", dueLabel: "August 31, 2026" },
      { month: "2026-08", monthLabel: "August 2026",    monthsRemaining: 11, dueDate: "2026-09-30", dueLabel: "September 30, 2026" },
      // Oct 31 2026 is a Saturday -> rolls to Monday.
      { month: "2026-09", monthLabel: "September 2026", monthsRemaining: 10, dueDate: "2026-11-02", dueLabel: "November 2, 2026" },
      { month: "2026-10", monthLabel: "October 2026",   monthsRemaining: 9,  dueDate: "2026-11-30", dueLabel: "November 30, 2026" },
      { month: "2026-11", monthLabel: "November 2026",  monthsRemaining: 8,  dueDate: "2026-12-31", dueLabel: "December 31, 2026" },
      // Jan 31 2027 is a Sunday -> rolls to Monday.
      { month: "2026-12", monthLabel: "December 2026",  monthsRemaining: 7,  dueDate: "2027-02-01", dueLabel: "February 1, 2027" },
      // Feb 28 2027 is a Sunday -> rolls to Monday.
      { month: "2027-01", monthLabel: "January 2027",   monthsRemaining: 6,  dueDate: "2027-03-01", dueLabel: "March 1, 2027" },
      { month: "2027-02", monthLabel: "February 2027",  monthsRemaining: 5,  dueDate: "2027-03-31", dueLabel: "March 31, 2027" },
      { month: "2027-03", monthLabel: "March 2027",     monthsRemaining: 4,  dueDate: "2027-04-30", dueLabel: "April 30, 2027" },
      // May 31 2027 is Memorial Day (federal holiday) -> rolls to Tuesday.
      { month: "2027-04", monthLabel: "April 2027",     monthsRemaining: 3,  dueDate: "2027-06-01", dueLabel: "June 1, 2027" },
      { month: "2027-05", monthLabel: "May 2027",       monthsRemaining: 2,  dueDate: "2027-06-30", dueLabel: "June 30, 2027" },
      // Jul 31 2027 is a Saturday -> rolls to Monday.
      { month: "2027-06", monthLabel: "June 2027",      monthsRemaining: 1,  dueDate: "2027-08-02", dueLabel: "August 2, 2027" },
    ],
  },
};

export const CURRENT_TAX_YEAR = "TY2026";

export const currentPeriod = () => TAX_PERIODS[CURRENT_TAX_YEAR];

/** Due-date rows for the current period, safe to render directly. */
export const dueDateRows = () => currentPeriod().dueDates;

/** Look up one first-used month. Returns undefined for a month outside the period. */
export const dueDateFor = (month) =>
  currentPeriod().dueDates.find((d) => d.month === month);

// Today as YYYY-MM-DD in the viewer's own time zone. toISOString() would give
// the UTC date, which rolls over hours early for US viewers in the evening.
const localIsoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * The earliest deadline in the current period that hasn't passed yet, as a
 * dueDates row - so "next deadline" copy follows the calendar instead of
 * hardcoding August 31. Picks from the transcribed IRS table above; it never
 * computes a date. Returns undefined once every deadline in the period has
 * passed: callers render nothing then, never an old date.
 */
export const nextDueDate = (today = new Date()) => {
  const iso = localIsoDate(today);
  return currentPeriod().dueDates.find((d) => d.dueDate >= iso);
};

/** "Nov 2" style label for a dueDates row, for tight spaces. */
export const shortDueLabel = (row) =>
  new Date(`${row.dueDate}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

/**
 * True when the configured period has ended and this file needs its annual
 * update. Callers should refuse to render deadlines rather than show stale ones -
 * a blank table is recoverable, a wrong deadline is not.
 */
export const isPeriodStale = (today = new Date()) =>
  today.toISOString().slice(0, 10) > currentPeriod().periodEnd;

/** Days until the current period ends. Used by the build check to warn early. */
export const daysUntilPeriodEnd = (today = new Date()) =>
  Math.ceil(
    (new Date(`${currentPeriod().periodEnd}T00:00:00Z`) - today) / 86_400_000
  );
